"use client";

import {
	RiCheckLine,
	RiExpandUpDownLine,
	RiMusic2Line,
} from "@remixicon/react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Button } from "@workspace/ui/components/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@workspace/ui/components/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@workspace/ui/components/popover";
import { useCallback, useMemo, useRef, useState } from "react";
import { getGenreDisplayName } from "@workspace/shared-utils";
import { cn } from "@/lib/utils";

export interface GenreFilterProps {
	value: string;
	onValueChange: (value: string) => void;
	availableGenres: string[];
	hasNoGenreArtists?: boolean;
	placeholder?: string;
	className?: string;
}

// Threshold for enabling virtualization
const VIRTUALIZATION_THRESHOLD = 20;

export function GenreFilter({
	value,
	onValueChange,
	availableGenres,
	hasNoGenreArtists = false,
	placeholder = "All Genres",
	className,
}: GenreFilterProps) {
	const [open, setOpen] = useState(false);
	const [searchValue, setSearchValue] = useState("");

	// Create the full options list
	const allOptions = useMemo(() => {
		const options = [
			{ value: "all", label: "All Genres" },
			...(hasNoGenreArtists
				? [{ value: "no-genre", label: "Unknown Genre" }]
				: []),
			...availableGenres.map((genre) => ({
				value: genre,
				label: getGenreDisplayName(genre),
			})),
		];
		return options;
	}, [availableGenres, hasNoGenreArtists]);

	// Filter options based on search
	const filteredOptions = useMemo(() => {
		if (!searchValue) return allOptions;
		const search = searchValue.toLowerCase();
		return allOptions.filter(
			(option) =>
				option.label.toLowerCase().includes(search) ||
				option.value.toLowerCase().includes(search),
		);
	}, [allOptions, searchValue]);

	// Determine if we should use virtualization
	const shouldVirtualize = availableGenres.length >= VIRTUALIZATION_THRESHOLD;

	// Get display label for selected value
	const selectedLabel =
		value === "all"
			? "All Genres"
			: value === "no-genre"
				? "Unknown Genre"
				: getGenreDisplayName(value);

	const handleSelect = useCallback(
		(selectedValue: string) => {
			onValueChange(selectedValue === value ? "all" : selectedValue);
			setOpen(false);
			setSearchValue("");
		},
		[onValueChange, value],
	);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					role="combobox"
					aria-expanded={open}
					aria-label="Select genre filter"
					className={cn("h-10 w-full justify-between px-3", className)}
				>
					<div className="flex items-center gap-2 truncate">
						<RiMusic2Line className="h-4 w-4 shrink-0" />
						<span className="truncate">
							{value === "all" ? placeholder : selectedLabel}
						</span>
					</div>
					<RiExpandUpDownLine className="h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[var(--radix-popover-trigger-width)] p-0"
				align="start"
				sideOffset={4}
			>
				<Command shouldFilter={false}>
					<CommandInput
						placeholder="Search genres..."
						value={searchValue}
						onValueChange={setSearchValue}
					/>
					<CommandList>
						<CommandEmpty>No genre found.</CommandEmpty>
						<CommandGroup>
							{shouldVirtualize ? (
								<VirtualizedOptions
									options={filteredOptions}
									selectedValue={value}
									onSelect={handleSelect}
								/>
							) : (
								<RegularOptions
									options={filteredOptions}
									selectedValue={value}
									onSelect={handleSelect}
								/>
							)}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}

// Regular options renderer (for small lists)
function RegularOptions({
	options,
	selectedValue,
	onSelect,
}: {
	options: Array<{ value: string; label: string }>;
	selectedValue: string;
	onSelect: (value: string) => void;
}) {
	// Separate "All Genres" from the rest
	const allGenresOption = options.find((opt) => opt.value === "all");
	const otherOptions = options.filter((opt) => opt.value !== "all");

	return (
		<>
			{allGenresOption && (
				<CommandItem
					key={allGenresOption.value}
					value={allGenresOption.value}
					onSelect={() => onSelect(allGenresOption.value)}
					className="cursor-pointer"
				>
					<div className="flex items-center justify-between w-full">
						<span className="truncate">{allGenresOption.label}</span>
						<RiCheckLine
							className={cn(
								"ml-2 h-4 w-4 shrink-0",
								selectedValue === allGenresOption.value ? "opacity-100" : "opacity-0",
							)}
						/>
					</div>
				</CommandItem>
			)}
			{selectedValue !== "all" && (
				<CommandItem
					value="clear-selection"
					onSelect={() => onSelect("all")}
					className="text-destructive cursor-pointer"
				>
					<span>Clear Selection</span>
				</CommandItem>
			)}
			{otherOptions.map((option) => (
				<CommandItem
					key={option.value}
					value={option.value}
					onSelect={() => onSelect(option.value)}
					className="cursor-pointer"
				>
					<div className="flex items-center justify-between w-full">
						<span className="truncate">{option.label}</span>
						<RiCheckLine
							className={cn(
								"ml-2 h-4 w-4 shrink-0",
								selectedValue === option.value ? "opacity-100" : "opacity-0",
							)}
						/>
					</div>
				</CommandItem>
			))}
		</>
	);
}

// Virtualized options renderer (for large lists)
function VirtualizedOptions({
	options,
	selectedValue,
	onSelect,
}: {
	options: Array<{ value: string; label: string }>;
	selectedValue: string;
	onSelect: (value: string) => void;
}) {
	const parentRef = useRef<HTMLDivElement>(null);

	// Separate "All Genres" from the rest
	const allGenresOption = options.find((opt) => opt.value === "all");
	const otherOptions = options.filter((opt) => opt.value !== "all");

	// Add clear selection item if needed
	const needsClearSelection = selectedValue !== "all";
	const itemsToVirtualize = needsClearSelection
		? [{ value: "clear-selection", label: "Clear Selection" }, ...otherOptions]
		: otherOptions;

	const virtualizer = useVirtualizer({
		count: itemsToVirtualize.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 32, // Height of each CommandItem
		overscan: 5,
	});

	return (
		<>
			{/* Always render "All Genres" first (non-virtualized) */}
			{allGenresOption && (
				<CommandItem
					key={allGenresOption.value}
					value={allGenresOption.value}
					onSelect={() => onSelect(allGenresOption.value)}
					className="cursor-pointer"
				>
					<div className="flex items-center justify-between w-full">
						<span className="truncate">{allGenresOption.label}</span>
						<RiCheckLine
							className={cn(
								"ml-2 h-4 w-4 shrink-0",
								selectedValue === allGenresOption.value ? "opacity-100" : "opacity-0",
							)}
						/>
					</div>
				</CommandItem>
			)}

			{/* Virtualized list */}
			<div
				ref={parentRef}
				className="max-h-[200px] overflow-auto"
				style={{
					height: Math.min(itemsToVirtualize.length * 32, 200),
				}}
			>
				<div
					style={{
						height: `${virtualizer.getTotalSize()}px`,
						width: "100%",
						position: "relative",
					}}
				>
					{virtualizer.getVirtualItems().map((virtualItem) => {
						const option = itemsToVirtualize[virtualItem.index];
						if (!option) return null;

						// Handle "Clear Selection" item
						if (option.value === "clear-selection") {
							return (
								<CommandItem
									key="clear-selection"
									value="clear-selection"
									onSelect={() => onSelect("all")}
									className="text-destructive cursor-pointer"
									style={{
										position: "absolute",
										top: 0,
										left: 0,
										width: "100%",
										height: `${virtualItem.size}px`,
										transform: `translateY(${virtualItem.start}px)`,
									}}
								>
									<span>Clear Selection</span>
								</CommandItem>
							);
						}

						return (
							<CommandItem
								key={virtualItem.key}
								value={option.value}
								onSelect={() => onSelect(option.value)}
								className="cursor-pointer"
								style={{
									position: "absolute",
									top: 0,
									left: 0,
									width: "100%",
									height: `${virtualItem.size}px`,
									transform: `translateY(${virtualItem.start}px)`,
								}}
							>
								<div className="flex items-center justify-between w-full">
									<span className="truncate">{option.label}</span>
									<RiCheckLine
										className={cn(
											"ml-2 h-4 w-4 shrink-0",
											selectedValue === option.value ? "opacity-100" : "opacity-0",
										)}
									/>
								</div>
							</CommandItem>
						);
					})}
				</div>
			</div>
		</>
	);
}
